import {
  InviteOutcomeEnum,
  NegativeBalanceResolutionEnum,
  PartnerStatusActionEnum,
  PartnerStatusEnum,
  ReferralHistoryActionEnum,
  ReferralInviteKindEnum,
  ReferralStatusEnum,
  ReferredPaymentKindEnum,
  StatementReviewActionEnum,
  StatementStatusEnum,
} from "types/partner/PartnerEnums";

import { MAX_INVITE_EMAILS_PER_REQUEST, PRO_TRIAL_PERIOD_DAYS } from "./partnerProgramDefaults";

/** Result line shown for each invited address. NOT_REFERABLE is deliberately reason-free. */
export const INVITE_OUTCOME_COPY: Record<InviteOutcomeEnum, string> = {
  [InviteOutcomeEnum.INVITED]: "Invited",
  [InviteOutcomeEnum.RESENT]: "Invite re-sent",
  [InviteOutcomeEnum.NOT_REFERABLE]: "Can't be referred",
  [InviteOutcomeEnum.DAILY_CAP_REACHED]: "Daily invite limit reached — try again tomorrow",
  [InviteOutcomeEnum.PRO_TRIAL_DISABLED]: "Pro-trial invites are turned off for your account",
  [InviteOutcomeEnum.PARTNER_SUSPENDED]: "Invites are paused while your partner access is suspended",
};

/** Label for each invite kind. */
export const INVITE_KIND_LABELS: Record<ReferralInviteKindEnum, string> = {
  [ReferralInviteKindEnum.PLAIN]: "Plain invite",
  [ReferralInviteKindEnum.PRO_TRIAL]: "Pro trial invite",
};

/** Label for each referral status. */
export const REFERRAL_STATUS_LABELS: Record<ReferralStatusEnum, string> = {
  [ReferralStatusEnum.PENDING]: "Pending",
  [ReferralStatusEnum.ACTIVE]: "Active",
  [ReferralStatusEnum.EXPIRED]: "Expired",
  [ReferralStatusEnum.ENDED]: "Ended",
  [ReferralStatusEnum.REVOKED]: "Revoked",
};

/** Label for each partner status. */
export const PARTNER_STATUS_LABELS: Record<PartnerStatusEnum, string> = {
  [PartnerStatusEnum.ACTIVE]: "Active",
  [PartnerStatusEnum.SUSPENDED]: "Suspended",
  [PartnerStatusEnum.ENDED]: "Ended",
  [PartnerStatusEnum.ENDED_FOR_CAUSE]: "Ended for cause",
};

/** Button label for each admin partner status action. */
export const PARTNER_STATUS_ACTION_LABELS: Record<PartnerStatusActionEnum, string> = {
  [PartnerStatusActionEnum.SUSPEND]: "Suspend",
  [PartnerStatusActionEnum.RESTORE]: "Restore",
  [PartnerStatusActionEnum.END]: "End",
  [PartnerStatusActionEnum.END_FOR_CAUSE]: "End for cause",
};

/** Statement status label shown to partners. A held statement reads "Open" so the review is not signalled to the partner. */
export const PARTNER_STATEMENT_STATUS_LABELS: Record<StatementStatusEnum, string> = {
  [StatementStatusEnum.OPEN]: "Open",
  [StatementStatusEnum.HELD_FOR_REVIEW]: "Open",
  [StatementStatusEnum.APPROVED]: "Approved",
  [StatementStatusEnum.PAID]: "Paid",
};

/** Statement status label shown to admins. */
export const ADMIN_STATEMENT_STATUS_LABELS: Record<StatementStatusEnum, string> = {
  [StatementStatusEnum.OPEN]: "Open",
  [StatementStatusEnum.HELD_FOR_REVIEW]: "Held for review",
  [StatementStatusEnum.APPROVED]: "Approved",
  [StatementStatusEnum.PAID]: "Paid",
};

/** Label for each kind of referred payment. */
export const REFERRED_PAYMENT_KIND_LABELS: Record<ReferredPaymentKindEnum, string> = {
  [ReferredPaymentKindEnum.SUBSCRIPTION]: "Subscription",
  [ReferredPaymentKindEnum.TOKEN_PURCHASE]: "Token purchase",
  [ReferredPaymentKindEnum.ORGANIZATION_CHARGE]: "Organization charge",
};

/** Label for each referral history action. */
export const REFERRAL_HISTORY_ACTION_LABELS: Record<ReferralHistoryActionEnum, string> = {
  [ReferralHistoryActionEnum.CREATED]: "Created",
  [ReferralHistoryActionEnum.RESENT]: "Re-sent",
  [ReferralHistoryActionEnum.ACTIVATED]: "Activated",
  [ReferralHistoryActionEnum.EXPIRED]: "Expired",
  [ReferralHistoryActionEnum.ENDED]: "Ended",
  [ReferralHistoryActionEnum.READDRESSED]: "Re-addressed",
  [ReferralHistoryActionEnum.REVOKED]: "Revoked",
};

/** Label for each way a negative balance is settled. */
export const NEGATIVE_BALANCE_RESOLUTION_LABELS: Record<NegativeBalanceResolutionEnum, string> = {
  [NegativeBalanceResolutionEnum.RECOVERED]: "Recovered under the agreement",
  [NegativeBalanceResolutionEnum.WRITTEN_OFF]: "Written off",
};

/** Marker shown wherever a Pro-trial invite or referral appears. */
export const PRO_TRIAL_MARKER_LABEL = "Pro trial";

/** Explains the cost and commission timing of a Pro-trial invite. */
export const PRO_TRIAL_INVITE_DESCRIPTION = `Gives the prospect a ${PRO_TRIAL_PERIOD_DAYS}-day Pro trial. It costs Muggle tokens and earns no commission until the customer's first paid charge.`;

/** Statement line explaining a negative carried balance. */
export const CARRIED_BALANCE_CLAWBACK_LABEL = "Refund clawback carried forward";

/** Statement line explaining a positive carried balance. */
export const CARRIED_BALANCE_BELOW_MINIMUM_LABEL = "Carried from a month below the minimum payout";

/** Title of the partner's dashboard page and its navigation entry. */
export const PARTNER_PROGRAM_TITLE = "Partner program";

/** Heading of the error state shown when the partner lookup fails for a reason other than "not a partner". */
export const PARTNER_ACCESS_ERROR_TITLE = "Couldn't load your partner access";

/** Body of the partner lookup error state. */
export const PARTNER_ACCESS_ERROR_DESCRIPTION = "Something went wrong on our side. Try again in a moment.";

/** Label of every retry button on the partner and partner-admin pages. */
export const PARTNER_RETRY_LABEL = "Try again";

/** Title of the card that asks the partner to accept the program agreement. */
export const PARTNER_AGREEMENT_TITLE = "Partner program agreement";

/** Label of the button that accepts the agreement. */
export const PARTNER_AGREEMENT_ACCEPT_LABEL = "Accept agreement";

/** Label of the button shown when the required agreement version has no bundled text. */
export const PARTNER_AGREEMENT_RELOAD_LABEL = "Reload page";

/** Shown instead of the clauses when the required version's text is not bundled in this build. */
export const PARTNER_AGREEMENT_UPDATED_MESSAGE =
  "The agreement has been updated. Reload the page to read the new version.";

/** Shown in the accepted-agreement disclosure when the accepted version's text is not bundled in this build. */
export const PARTNER_AGREEMENT_TEXT_UNAVAILABLE_MESSAGE =
  "The text of this agreement version is not available in this app.";

/** Shown when accepting the agreement fails without a server sentence. */
export const PARTNER_AGREEMENT_ACCEPT_FALLBACK_ERROR = "Couldn't record your acceptance. Try again.";

/** Label of the disclosure that reveals the accepted agreement text. */
export const PARTNER_AGREEMENT_VIEW_LABEL = "View agreement";

/** Label of the disclosure that hides the accepted agreement text. */
export const PARTNER_AGREEMENT_HIDE_LABEL = "Hide agreement";

/** Title of the card listing the partner's commercial terms. */
export const PARTNER_TERMS_TITLE = "Your partner terms";

/** Row labels of the partner terms card. */
export const PARTNER_TERMS_ROW_LABELS = {
  commissionRate: "Commission rate",
  claimWindow: "Claim window",
  earningWindow: "Earning window",
  holdPeriod: "Hold period",
  minimumPayout: "Minimum payout",
  status: "Status",
  agreement: "Agreement",
} as const;

/** Title of the invite card. */
export const PARTNER_INVITE_TITLE = "Invite people";

/** Label of the invite address textarea. */
export const PARTNER_INVITE_EMAILS_LABEL = "Email addresses";

/** Helper under the invite address textarea. */
export const PARTNER_INVITE_EMAILS_HELPER = `Separate addresses with commas, spaces, or new lines. Up to ${MAX_INVITE_EMAILS_PER_REQUEST} per batch.`;

/** Label of the invite kind radio group. */
export const PARTNER_INVITE_KIND_GROUP_LABEL = "Invite kind";

/** Label of the submit button of the invite form. */
export const PARTNER_INVITE_SUBMIT_LABEL = "Send invites";

/** Shown when the invite form is submitted with no address. */
export const PARTNER_INVITE_EMPTY_BATCH_ERROR = "Enter at least one email address.";

/** Shown when the invite form is submitted with more addresses than one batch allows. */
export const PARTNER_INVITE_OVERSIZED_BATCH_ERROR = `Enter at most ${MAX_INVITE_EMAILS_PER_REQUEST} addresses per batch.`;

/** Shown when sending invites fails without a server sentence. */
export const PARTNER_INVITE_SEND_FALLBACK_ERROR = "Couldn't send invites. Try again.";

/** Notice shown with the disabled invite controls of a suspended partner. */
export const PARTNER_INVITES_SUSPENDED_NOTICE =
  "Your partner access is suspended. Existing referrals keep earning; new invites are paused.";

/** Notice shown with the disabled invite controls of an ended partner. */
export const PARTNER_INVITES_ENDED_NOTICE =
  "Your partnership has ended. Active referrals keep earning through their earning windows; new invites are closed.";

/** Notice that disables the invite form for a partner in each status; null where invites are open. */
export const PARTNER_INVITES_PAUSED_NOTICE_BY_STATUS: Record<PartnerStatusEnum, string | null> = {
  [PartnerStatusEnum.ACTIVE]: null,
  [PartnerStatusEnum.SUSPENDED]: PARTNER_INVITES_SUSPENDED_NOTICE,
  [PartnerStatusEnum.ENDED]: PARTNER_INVITES_ENDED_NOTICE,
  [PartnerStatusEnum.ENDED_FOR_CAUSE]: PARTNER_INVITES_ENDED_NOTICE,
};

/** Title of the per-address invite results list. */
export const PARTNER_INVITE_OUTCOMES_TITLE = "Invite results";

/** Title of the referrals card. */
export const PARTNER_REFERRALS_TITLE = "Referrals";

/** Shown in the referrals card before any invite exists. */
export const PARTNER_REFERRALS_EMPTY = "No invites yet.";

/** Column headers of the referrals table. */
export const PARTNER_REFERRAL_COLUMN_LABELS = {
  email: "Email",
  status: "Status",
  invited: "Invited",
  claimWindowEnds: "Claim window ends",
  earningWindowEnds: "Earning window ends",
  commissionEarned: "Commission earned",
} as const;

/** Label of the button that expands a referral row to its payments. */
export const PARTNER_REFERRAL_PAYMENTS_TOGGLE_LABEL = "Payments";

/** Shown in an expanded referral row with no payments. */
export const PARTNER_REFERRAL_PAYMENTS_EMPTY = "No payments yet.";

/** Column headers of the referral payments table. */
export const PARTNER_PAYMENT_COLUMN_LABELS = {
  date: "Date",
  kind: "Kind",
  collected: "Collected",
  refunded: "Refunded",
  commission: "Commission",
} as const;

/** Shown when a referral's payments fail to load without a server sentence. */
export const PARTNER_REFERRAL_PAYMENTS_FALLBACK_ERROR = "Couldn't load payments. Collapse and expand to retry.";

/** Title of the statements card. */
export const PARTNER_STATEMENTS_TITLE = "Statements";

/** Shown in the statements card before any statement exists. */
export const PARTNER_STATEMENTS_EMPTY = "No statements yet.";

/** Column headers of the statements table. */
export const PARTNER_STATEMENT_COLUMN_LABELS = {
  month: "Month",
  status: "Status",
  entries: "Entries",
  carriedBalance: "Carried balance",
  payable: "Payable",
  payout: "Payout",
} as const;

/** Shown in a table cell that has no value. */
export const PARTNER_EMPTY_CELL = "—";

/** Shown above a partner list when a page of it fails to load. */
export const PARTNER_LIST_LOAD_ERROR = "Couldn't load this list. Try again in a moment.";

/** Label of the button that appends the next page of a partner list. */
export const PARTNER_LIST_LOAD_MORE_LABEL = "Load more";

/** Label of the load-more button while the next page is on its way. */
export const PARTNER_LIST_LOADING_MORE_LABEL = "Loading more";

/** Title of the admin partners page. */
export const ADMIN_PARTNERS_TITLE = "Partners";

/** Title of the create-partner card. */
export const ADMIN_PARTNER_CREATE_TITLE = "Add a partner";

/** Label of the create-partner account email input. */
export const ADMIN_PARTNER_EMAIL_LABEL = "Account email";

/** Label of the create-partner display name input. */
export const ADMIN_PARTNER_DISPLAY_NAME_LABEL = "Display name";

/** Label of the create-partner submit button. */
export const ADMIN_PARTNER_CREATE_SUBMIT_LABEL = "Create partner";

/** Shown when creating a partner fails without a server sentence. */
export const ADMIN_PARTNER_CREATE_FALLBACK_ERROR = "Couldn't create the partner. Try again.";

/** Labels of the partner terms form inputs. */
export const PARTNER_TERMS_FIELD_LABELS = {
  commissionRatePercent: "Commission rate (%)",
  claimWindowDays: "Claim window (days)",
  earningWindowMonths: "Earning window (months)",
  holdPeriodDays: "Hold period (days)",
  minimumPayoutUsd: "Minimum payout (USD)",
  dailyInviteCap: "Daily invite cap",
} as const;

/** Label of the switch that allows a partner to send Pro-trial invites. */
export const PARTNER_PRO_TRIAL_SWITCH_LABEL = "Allow Pro-trial invites";

/** Shown under a percent field whose text is not a 0-100 percentage. */
export const PARTNER_TERMS_PERCENT_ERROR = "Enter a percentage from 0 to 100.";

/** Shown under a count field whose text is not a whole number of 1 or more. */
export const PARTNER_TERMS_WHOLE_NUMBER_ERROR = "Enter a whole number of 1 or more.";

/** Shown under a dollar field whose text is not an amount in dollars. */
export const PARTNER_TERMS_DOLLAR_ERROR = "Enter an amount in dollars, like 50 or 49.99.";

/** Shown in the admin partners table before any partner exists. */
export const ADMIN_PARTNERS_EMPTY = "No partners yet.";

/** Column headers of the admin partners table. */
export const ADMIN_PARTNER_COLUMN_LABELS = {
  partner: "Partner",
  status: "Status",
  commission: "Commission",
  windows: "Windows",
  invitesToday: "Invites today",
  proTrialInvites: "Pro-trial invites",
} as const;

/** Pro-trial invite setting as shown in the admin partners table when allowed. */
export const ADMIN_PARTNER_PRO_TRIAL_ON_LABEL = "On";

/** Pro-trial invite setting as shown in the admin partners table when not allowed. */
export const ADMIN_PARTNER_PRO_TRIAL_OFF_LABEL = "Off";

/** Label of the button that expands a partner row to its stats. */
export const ADMIN_PARTNER_STATS_TOGGLE_LABEL = "Stats";

/** Label of the button that edits a partner's terms. */
export const ADMIN_PARTNER_EDIT_TERMS_LABEL = "Edit terms";

/** Labels of the partner stats panel. */
export const ADMIN_PARTNER_STATS_LABELS = {
  invitesSent: "Invites sent",
  referrals: "Referrals",
  partnerReferredRevenue: "Partner-referred revenue",
  commissionAccrued: "Commission accrued",
  commissionPaid: "Commission paid",
  proTrialGroup: "Pro-trial cost",
  proTrialInvites: "Pro-trial invites",
  proTrialsConverted: "Converted to paid",
  proTrialTokenCost: "Token cost",
} as const;

/** Title of the edit-terms dialog. */
export const ADMIN_PARTNER_EDIT_TERMS_TITLE = "Edit partner terms";

/** Which edits reach existing referrals and which only new ones. */
export const ADMIN_PARTNER_EDIT_TERMS_NOTE =
  "Rate, claim window and earning window apply to new referrals only. Hold period, minimum payout, invite cap and Pro-trial access apply immediately.";

/** Label of the edit-terms submit button. */
export const ADMIN_PARTNER_EDIT_TERMS_SUBMIT_LABEL = "Save terms";

/** Shown when saving partner terms fails without a server sentence. */
export const ADMIN_PARTNER_EDIT_TERMS_FALLBACK_ERROR = "Couldn't save the terms. Try again.";

/** Dialog title of each admin partner status action. */
export const PARTNER_STATUS_ACTION_DIALOG_TITLES: Record<PartnerStatusActionEnum, string> = {
  [PartnerStatusActionEnum.SUSPEND]: "Suspend partner",
  [PartnerStatusActionEnum.RESTORE]: "Restore partner",
  [PartnerStatusActionEnum.END]: "End partner",
  [PartnerStatusActionEnum.END_FOR_CAUSE]: "End partner for cause",
};

/** Dialog description of each admin partner status action. */
export const PARTNER_STATUS_ACTION_DIALOG_DESCRIPTIONS: Record<PartnerStatusActionEnum, string> = {
  [PartnerStatusActionEnum.SUSPEND]:
    "Blocks new invites. Existing referrals keep earning. You can restore access later.",
  [PartnerStatusActionEnum.RESTORE]: "Lets this partner send invites again.",
  [PartnerStatusActionEnum.END]:
    "Permanent. Pending referrals expire now; active referrals keep earning through their earning windows.",
  [PartnerStatusActionEnum.END_FOR_CAUSE]: "Permanent. Revokes every referral and reverses unpaid commission.",
};

/** Question above the resolution choice when ending a partner with a negative balance. */
export const PARTNER_NEGATIVE_BALANCE_PROMPT = "This partner has a negative balance. How was it resolved?";

/** Shown when changing a partner's status fails without a server sentence. */
export const PARTNER_STATUS_CHANGE_FALLBACK_ERROR = "Couldn't change the partner's status. Try again.";

/** Title of the admin referrals page and its navigation entry. */
export const ADMIN_REFERRALS_TITLE = "Referrals";

/** Shown in the admin referrals table when the search matches nothing. */
export const ADMIN_REFERRALS_EMPTY = "No referrals match this search.";

/** Labels of the admin referral search form controls. */
export const ADMIN_REFERRAL_SEARCH_LABELS = {
  partner: "Partner",
  allPartners: "All partners",
  email: "Email",
  status: "Status",
  allStatuses: "All statuses",
  submit: "Search",
} as const;

/** Shown in place of the partner filter when its options fail to load. */
export const ADMIN_REFERRAL_PARTNER_FILTER_ERROR = "Couldn't load the partner filter. Reload the page to retry.";

/** Column headers of the admin referrals table. */
export const ADMIN_REFERRAL_COLUMN_LABELS = {
  email: "Email",
  partner: "Partner",
  status: "Status",
  invited: "Invited",
  claimWindowEnds: "Claim window ends",
  commissionEarned: "Commission earned",
} as const;

/** Label of the button that expands a referral row to its history. */
export const ADMIN_REFERRAL_HISTORY_TOGGLE_LABEL = "History";

/** Label of the referral action that changes the invited address. */
export const ADMIN_REFERRAL_READDRESS_LABEL = "Re-address";

/** Label of the referral action that ends a referral for cause. */
export const ADMIN_REFERRAL_REVOKE_LABEL = "Revoke";

/** Title of the re-address dialog. */
export const ADMIN_REFERRAL_READDRESS_TITLE = "Re-address referral";

/** Description of the re-address dialog. */
export const ADMIN_REFERRAL_READDRESS_DESCRIPTION =
  "Keeps this referral's partner, terms, and claim window. Only the address changes.";

/** Label of the new-address input in the re-address dialog. */
export const ADMIN_REFERRAL_NEW_EMAIL_LABEL = "New email address";

/** Title of the revoke dialog. */
export const ADMIN_REFERRAL_REVOKE_TITLE = "Revoke referral";

/** Description of the revoke dialog. */
export const ADMIN_REFERRAL_REVOKE_DESCRIPTION =
  "Ends this referral for cause. Unpaid commission is reversed; paid commission stays paid.";

/** Shown when re-addressing fails without a server sentence. */
export const ADMIN_REFERRAL_READDRESS_FALLBACK_ERROR = "Couldn't re-address the referral. Try again.";

/** Shown when revoking fails without a server sentence. */
export const ADMIN_REFERRAL_REVOKE_FALLBACK_ERROR = "Couldn't revoke the referral. Try again.";

/** Actor shown on a history entry that no person performed. */
export const REFERRAL_HISTORY_SYSTEM_ACTOR_LABEL = "System";

/** Shown in an expanded referral row with no history. */
export const ADMIN_REFERRAL_HISTORY_EMPTY = "No history yet.";

/** Prefix of the line naming the address a re-addressed referral was first invited at. */
export const ADMIN_REFERRAL_INVITED_AS_PREFIX = "Invited as";

/** Prefix of the line giving why a referral was revoked. */
export const ADMIN_REFERRAL_REVOKE_REASON_PREFIX = "Revoke reason:";

/** What the copy toast says was copied from a referral row's id button. */
export const ADMIN_REFERRAL_ID_COPY_SUBJECT = "Referral ID";

/** Accessible name prefix of a referral row's id button, followed by the id. */
export const ADMIN_REFERRAL_COPY_ID_LABEL_PREFIX = "Copy referral ID";

/** Title of the admin partner statements page and its navigation entry. */
export const ADMIN_PARTNER_STATEMENTS_TITLE = "Partner Statements";

/** Shown in the admin statements table when the filters match nothing. */
export const ADMIN_STATEMENTS_EMPTY = "No statements match these filters.";

/** Labels of the admin statement filter controls. */
export const ADMIN_STATEMENT_FILTER_LABELS = {
  month: "Month",
  status: "Status",
  allStatuses: "All statuses",
} as const;

/** Placeholder of the month filter, seen only in browsers that render a month input as plain text. */
export const ADMIN_STATEMENT_MONTH_FORMAT_HINT = "YYYY-MM";

/** Column headers of the admin statements table. */
export const ADMIN_STATEMENT_COLUMN_LABELS = {
  partner: "Partner",
  month: "Month",
  status: "Status",
  entries: "Entries",
  carriedBalance: "Carried balance",
  payable: "Payable",
  refundRate: "Refund rate (90 days)",
  flags: "Flags",
  payout: "Payout",
  action: "Action",
} as const;

/** Prefix of the flag naming referrals whose card matched another referral's or the partner's own. */
export const ADMIN_STATEMENT_SHARED_CARD_PREFIX = "Shared card:";

/** Shown in place of the payout action on an approved statement with nothing payable. */
export const ADMIN_STATEMENT_NOTHING_TO_PAY_LABEL = "Nothing to pay";

/** Button label of each statement review action; the payout label is also the payout dialog's confirm button. */
export const STATEMENT_REVIEW_ACTION_LABELS: Record<StatementReviewActionEnum, string> = {
  [StatementReviewActionEnum.RELEASE]: "Release",
  [StatementReviewActionEnum.APPROVE]: "Approve",
  [StatementReviewActionEnum.RECORD_PAYOUT]: "Record payout",
};

/** Title of the release dialog. */
export const ADMIN_STATEMENT_RELEASE_TITLE = "Release statement";

/** Description of the release dialog. */
export const ADMIN_STATEMENT_RELEASE_DESCRIPTION = "Moves this statement out of review so it can be approved.";

/** Label of the button that dismisses a partner admin dialog. */
export const PARTNER_DIALOG_CANCEL_LABEL = "Cancel";

/** Label of the reason field of every partner admin action that records one. */
export const PARTNER_REASON_LABEL = "Reason";

/** Separator between the fields of a one-line partner summary, such as a dialog's target line. */
export const PARTNER_SUMMARY_FIELD_SEPARATOR = " · ";

/** Description of the record-payout dialog. */
export const ADMIN_STATEMENT_PAYOUT_DESCRIPTION = "Records that this statement was paid outside Muggle.";

/** Labels and placeholder of the record-payout dialog fields. */
export const ADMIN_STATEMENT_PAYOUT_FIELD_LABELS = {
  method: "Payout method",
  methodPlaceholder: "e.g. Wire, PayPal",
  reference: "Payout reference",
  paidOn: "Paid on",
} as const;

/** Shown when releasing a statement fails without a server sentence. */
export const ADMIN_STATEMENT_RELEASE_FALLBACK_ERROR = "Couldn't release the statement. Try again.";

/** Shown when approving a statement fails without a server sentence. */
export const ADMIN_STATEMENT_APPROVE_FALLBACK_ERROR = "Couldn't approve the statement. Try again.";

/** Shown when recording a payout fails without a server sentence. */
export const ADMIN_STATEMENT_PAYOUT_FALLBACK_ERROR = "Couldn't record the payout. Try again.";

/** Title of the admin source-of-sales report page and its navigation entry. */
export const ADMIN_SOURCE_OF_SALES_TITLE = "Source of Sales";

/** Title of the source-of-sales report card. */
export const ADMIN_SOURCE_OF_SALES_CARD_TITLE = "Source of sales";

/** Labels of the source-of-sales form controls. */
export const ADMIN_SOURCE_OF_SALES_FORM_LABELS = {
  from: "From (inclusive)",
  to: "To (exclusive)",
  submit: "Run report",
} as const;

/** Labels of the three source-of-sales figures. */
export const ADMIN_SOURCE_OF_SALES_RESULT_LABELS = {
  partnerReferredRevenue: "Partner-referred revenue",
  totalRevenue: "Total revenue",
  partnerReferredShare: "Partner-referred share",
} as const;

/** Shown when a source-of-sales date is missing. */
export const ADMIN_SOURCE_OF_SALES_MISSING_DATE_ERROR = "Choose a start and end date.";

/** Shown when the source-of-sales end date is not after the start date. */
export const ADMIN_SOURCE_OF_SALES_RANGE_ERROR = "The end date must be after the start date.";

/** Shown when the source-of-sales report fails without a server sentence. */
export const ADMIN_SOURCE_OF_SALES_FALLBACK_ERROR = "Couldn't run the report. Try again.";
